import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { MessageSquare, CreditCard, Video, Send, Loader2, CheckCircle2, XCircle, MinusCircle } from "lucide-react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { api, formatApiErrorDetail } from "../lib/api";

const KEEP_MASK_PREFIX = "••••";

function StatusBadge({ mockMode, lastTest }) {
  if (mockMode) {
    return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground"><MinusCircle className="h-3 w-3" />Mock mode</span>;
  }
  if (lastTest === "ok") {
    return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700"><CheckCircle2 className="h-3 w-3" />Connected</span>;
  }
  if (lastTest === "fail") {
    return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-700"><XCircle className="h-3 w-3" />Error</span>;
  }
  return <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Live · not tested</span>;
}

function Card({ icon: Icon, title, badge, children, footer }) {
  return (
    <section className="bg-white border border-border rounded-2xl p-5 shadow-sm space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center"><Icon className="h-5 w-5" /></span>
          <div><h3 className="font-semibold tracking-tight">{title}</h3></div>
        </div>
        {badge}
      </div>
      <div className="space-y-3">{children}</div>
      {footer && <div className="pt-2 border-t border-border flex flex-wrap gap-2">{footer}</div>}
    </section>
  );
}

export default function AdminIntegrations() {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tests, setTests] = useState({});
  const [smsModal, setSmsModal] = useState(false);
  const [smsPhone, setSmsPhone] = useState("+91");
  const [smsBusy, setSmsBusy] = useState(false);
  const [smsResult, setSmsResult] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/integrations");
    setCfg(data);
    setDraft({
      fast2sms: { ...data.fast2sms },
      razorpay: { ...data.razorpay },
      agora: { ...data.agora },
    });
  }, []);
  useEffect(() => { load(); }, [load]);

  const upd = (provider, key) => (value) => setDraft((d) => ({ ...d, [provider]: { ...d[provider], [key]: value } }));

  const buildPatch = (provider) => {
    // Strip masked sentinel values: a string still starting with mask means "keep" — backend ignores those.
    const out = {};
    for (const k of Object.keys(draft[provider] || {})) {
      out[k] = draft[provider][k];
    }
    return out;
  };

  const save = async (provider) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/admin/integrations", { [provider]: buildPatch(provider) });
      setCfg(data);
      setDraft({ fast2sms: { ...data.fast2sms }, razorpay: { ...data.razorpay }, agora: { ...data.agora } });
      toast.success(`${provider} settings saved`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const testRazorpay = async () => {
    setTests((t) => ({ ...t, razorpay: "loading" }));
    try {
      const { data } = await api.post("/admin/integrations/test/razorpay");
      toast.success(data.mock ? `Mock order created (${data.order_id})` : `Live order created: ${data.order_id}`);
      setTests((t) => ({ ...t, razorpay: "ok" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setTests((t) => ({ ...t, razorpay: "fail" }));
    }
  };
  const testAgora = async () => {
    setTests((t) => ({ ...t, agora: "loading" }));
    try {
      const { data } = await api.post("/admin/integrations/test/agora");
      if (!data.ok) { toast.error(data.error || "Test failed"); setTests((t) => ({ ...t, agora: "fail" })); return; }
      toast.success(`Token via ${data.mode}: ${(data.token || "").slice(0, 24)}…`);
      setTests((t) => ({ ...t, agora: "ok" }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setTests((t) => ({ ...t, agora: "fail" }));
    }
  };
  const sendTestSms = async () => {
    setSmsBusy(true); setSmsResult(null);
    try {
      const { data } = await api.post("/admin/integrations/test/fast2sms", { phone: smsPhone });
      setSmsResult(data);
      setTests((t) => ({ ...t, fast2sms: data.ok ? "ok" : "fail" }));
    } catch (e) {
      setSmsResult({ ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message });
      setTests((t) => ({ ...t, fast2sms: "fail" }));
    } finally { setSmsBusy(false); }
  };

  if (!cfg || !draft) return <AdminLayout title="Integrations"><div className="text-muted-foreground">Loading…</div></AdminLayout>;
  const mock = cfg.mock_mode || {};

  return (
    <AdminLayout title="Integrations" subtitle="Configure real providers or stay in mock mode">
      <div data-testid="admin-integrations-page" className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card
          icon={MessageSquare}
          title="Fast2SMS (OTP)"
          badge={<StatusBadge mockMode={mock.fast2sms} lastTest={tests.fast2sms} />}
          footer={(
            <>
              <Button data-testid="fast2sms-save" variant="outline" onClick={() => save("fast2sms")} disabled={saving} className="rounded-full">Save</Button>
              <Button data-testid="fast2sms-test" onClick={() => { setSmsResult(null); setSmsModal(true); }} className="rounded-full"><Send className="h-3.5 w-3.5 mr-1.5" />Test send</Button>
            </>
          )}
        >
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="fast2sms-enabled" checked={!!draft.fast2sms.enabled} onCheckedChange={(v) => upd("fast2sms", "enabled")(v)} />Enabled</label>
          <div className="space-y-1.5"><label className="text-xs font-medium">API Key</label>
            <Input data-testid="fast2sms-apikey" type="password" placeholder={cfg.fast2sms.api_key || "Enter API key"} value={draft.fast2sms.api_key?.startsWith(KEEP_MASK_PREFIX) ? "" : draft.fast2sms.api_key || ""} onChange={(e) => upd("fast2sms", "api_key")(e.target.value)} />
            {cfg.fast2sms.api_key && <div className="text-xs text-muted-foreground">Stored: {cfg.fast2sms.api_key}</div>}</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><label className="text-xs font-medium">Sender ID</label>
              <Input data-testid="fast2sms-sender" value={draft.fast2sms.sender_id || ""} onChange={(e) => upd("fast2sms", "sender_id")(e.target.value)} placeholder="OTTCHL" /></div>
            <div className="space-y-1.5"><label className="text-xs font-medium">Route</label>
              <Select value={draft.fast2sms.route || "dlt"} onValueChange={(v) => upd("fast2sms", "route")(v)}>
                <SelectTrigger data-testid="fast2sms-route"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dlt">DLT (Recommended)</SelectItem>
                  <SelectItem value="otp">OTP</SelectItem>
                  <SelectItem value="v3">v3</SelectItem>
                </SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1.5"><label className="text-xs font-medium">DLT Template ID</label>
            <Input data-testid="fast2sms-template-id" value={draft.fast2sms.dlt_template_id || ""} onChange={(e) => upd("fast2sms", "dlt_template_id")(e.target.value)} placeholder="e.g 11111" />
            <div className="text-xs text-muted-foreground">Enter the DLT template ID registered with Fast2SMS.</div></div>
          <div className="space-y-1.5"><label className="text-xs font-medium">Template Text (reference only)</label>
            <Textarea data-testid="fast2sms-template-text" rows={2} value={draft.fast2sms.template_text || ""} onChange={(e) => upd("fast2sms", "template_text")(e.target.value)} placeholder="Dear user, your OTP for the Mobile verification is {#var#}. Please do no share it with anyone. -FLICI" className="text-xs" />
            <div className="text-xs text-muted-foreground"><code className="bg-muted px-1 rounded">{`{#var#}`}</code> will be replaced with the actual OTP code automatically by Fast2SMS.</div></div>
        </Card>

        <Card
          icon={CreditCard}
          title="Razorpay (Payments)"
          badge={<StatusBadge mockMode={mock.razorpay} lastTest={tests.razorpay} />}
          footer={(
            <>
              <Button data-testid="razorpay-save" variant="outline" onClick={() => save("razorpay")} disabled={saving} className="rounded-full">Save</Button>
              <Button data-testid="razorpay-test" onClick={testRazorpay} className="rounded-full">{tests.razorpay === "loading" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}Test order</Button>
            </>
          )}
        >
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="razorpay-enabled" checked={!!draft.razorpay.enabled} onCheckedChange={(v) => upd("razorpay", "enabled")(v)} />Enabled</label>
          <div className="space-y-1.5"><label className="text-xs font-medium">Key ID</label>
            <Input data-testid="razorpay-keyid" value={draft.razorpay.key_id || ""} onChange={(e) => upd("razorpay", "key_id")(e.target.value)} placeholder="rzp_test_..." /></div>
          <div className="space-y-1.5"><label className="text-xs font-medium">Key Secret</label>
            <Input data-testid="razorpay-secret" type="password" placeholder={cfg.razorpay.key_secret || "Enter secret"} value={draft.razorpay.key_secret?.startsWith(KEEP_MASK_PREFIX) ? "" : draft.razorpay.key_secret || ""} onChange={(e) => upd("razorpay", "key_secret")(e.target.value)} />
            {cfg.razorpay.key_secret && <div className="text-xs text-muted-foreground">Stored: {cfg.razorpay.key_secret}</div>}</div>
        </Card>

        <Card
          icon={Video}
          title="Agora (Video)"
          badge={<StatusBadge mockMode={mock.agora} lastTest={tests.agora} />}
          footer={(
            <>
              <Button data-testid="agora-save" variant="outline" onClick={() => save("agora")} disabled={saving} className="rounded-full">Save</Button>
              <Button data-testid="agora-test" onClick={testAgora} className="rounded-full">{tests.agora === "loading" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}Test token</Button>
            </>
          )}
        >
          <label className="flex items-center gap-2 text-sm"><Switch data-testid="agora-enabled" checked={!!draft.agora.enabled} onCheckedChange={(v) => upd("agora", "enabled")(v)} />Enabled</label>
          <div className="space-y-1.5"><label className="text-xs font-medium">App ID</label>
            <Input data-testid="agora-appid" value={draft.agora.app_id || ""} onChange={(e) => upd("agora", "app_id")(e.target.value)} /></div>
          <div className="space-y-1.5"><label className="text-xs font-medium">App Certificate</label>
            <Input data-testid="agora-cert" type="password" placeholder={cfg.agora.app_certificate || "Enter certificate"} value={draft.agora.app_certificate?.startsWith(KEEP_MASK_PREFIX) ? "" : draft.agora.app_certificate || ""} onChange={(e) => upd("agora", "app_certificate")(e.target.value)} />
            {cfg.agora.app_certificate && <div className="text-xs text-muted-foreground">Stored: {cfg.agora.app_certificate}</div>}</div>
          <div className="space-y-1.5"><label className="text-xs font-medium">Temp Token (dev fallback, optional)</label>
            <Textarea data-testid="agora-temptoken" rows={2} placeholder={cfg.agora.temp_token || "Paste temp token"} value={draft.agora.temp_token?.startsWith(KEEP_MASK_PREFIX) ? "" : draft.agora.temp_token || ""} onChange={(e) => upd("agora", "temp_token")(e.target.value)} className="font-mono text-xs" />
            {cfg.agora.temp_token && <div className="text-xs text-muted-foreground">Stored: {cfg.agora.temp_token}</div>}</div>
        </Card>
      </div>

      <Dialog open={smsModal} onOpenChange={setSmsModal}>
        <DialogContent data-testid="fast2sms-test-modal" className="sm:max-w-md rounded-2xl">
          <DialogHeader><DialogTitle>Send test OTP via Fast2SMS</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">A test code "123456" will be sent. Useful for sanity-checking your API key + route.</p>
          <Input data-testid="fast2sms-test-phone" value={smsPhone} onChange={(e) => setSmsPhone(e.target.value)} placeholder="+91XXXXXXXXXX" />
          {smsResult && (
            <div data-testid="fast2sms-test-result" className={"text-xs p-3 rounded-xl " + (smsResult.ok ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800")}>
              <div className="font-semibold">{smsResult.ok ? (smsResult.mock ? "Mock send — config disabled" : "Sent!") : "Error"}</div>
              <pre className="mt-1 whitespace-pre-wrap break-words text-[10px]">{JSON.stringify(smsResult, null, 2)}</pre>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSmsModal(false)} className="rounded-full">Close</Button>
            <Button data-testid="fast2sms-test-send" onClick={sendTestSms} disabled={smsBusy || !smsPhone.startsWith("+91")} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground hover:text-primary-foreground">
              {smsBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
